import { useState, useCallback, type FormEvent } from 'react';
import PageSEO from '@/components/feature/PageSEO';
import Breadcrumb from '@/components/feature/Breadcrumb';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9rd8q7nahb7382ovkt0';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '今日同人库',
  url: 'https://todaydoujin.com/',
  description: '今日同人库是专为同人爱好者打造的每日内容聚合平台，汇聚最新同人小说、同人画作、同人音乐与周边资讯。',
  foundingDate: '2024',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'contact@todaydoujin.com',
    availableLanguage: ['Chinese'],
  },
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '今日同人库是什么？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '今日同人库是一个专为同人爱好者打造的每日内容聚合平台。我们每日精选优质的原创同人小说、同人画作、同人音乐和周边资讯，帮助同人爱好者发现更多精彩内容。',
      },
    },
    {
      '@type': 'Question',
      name: '如何向今日同人库投稿？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '我们欢迎所有同人创作者的投稿！你可以通过本页面的"联系我们"表单发送作品链接和简介，我们的编辑团队会在3个工作日内审核并回复。',
      },
    },
    {
      '@type': 'Question',
      name: '今日同人库的内容多久更新一次？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '今日同人库每天更新！我们每日精选最新的同人作品，确保你每次访问都能发现新鲜有趣的内容。订阅我们的邮件通知可以第一时间收到每日精选推送。',
      },
    },
    {
      '@type': 'Question',
      name: '今日同人库是否收费？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '今日同人库完全免费！我们的使命是推广优质的同人创作，让更多人感受到同人文化的魅力。所有内容均可免费浏览，订阅邮件通知也不会产生任何费用。',
      },
    },
    {
      '@type': 'Question',
      name: '如何成为今日同人库的合作伙伴？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '如果你是同人展会主办方、同人社团组织者或其他同人相关机构，欢迎通过"联系我们"表单与我们取得联系，我们会尽快与你沟通合作事宜。',
      },
    },
  ],
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const formEl = e.currentTarget;
      const formData = new FormData(formEl);

      const honeypot = (formData.get('phone_alt') as string || '').trim();
      if (honeypot) {
        setFormStatus('success');
        formEl.reset();
        return;
      }

      setFormStatus('loading');
      setFormError('');

      try {
        const res = await fetch(CONTACT_FORM_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams(
            Array.from(formData.entries())
              .filter(([key]) => key !== 'phone_alt')
              .map(([key, value]) => [key, value.toString()])
          ).toString(),
        });

        const responseText = await res.text();
        let parsed;
        try { parsed = JSON.parse(responseText); } catch { parsed = null; }

        if (res.ok && parsed?.code === 'OK') {
          setFormStatus('success');
          formEl.reset();
        } else {
          const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '发送失败，请稍后再试';
          setFormError(serverMsg);
          setFormStatus('error');
        }
      } catch {
        setFormError('网络错误，请检查网络连接后重试');
        setFormStatus('error');
      }
    },
    [],
  );

  return (
    <>
      <PageSEO
        title="关于我们"
        description="了解今日同人库的品牌故事、核心使命与团队。我们是专为同人爱好者打造的每日内容聚合平台，致力于推广优质同人创作。"
        keywords="今日同人库,关于我们,同人平台,品牌介绍,联系我们"
        canonicalUrl="/about"
        jsonLd={[aboutJsonLd, faqJsonLd]}
      />

      <div className="pt-20">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-6">
              关于今日同人库
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8 max-w-2xl mx-auto">
              今日同人库诞生于2024年，由一群热爱同人文化的创作者和读者共同发起。我们相信，每一份同人创作都承载着创作者最真挚的热爱与才华，而我们的使命，就是让这些美好的作品被更多人看见。
            </p>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {[
                {
                  icon: 'ri-heart-line',
                  title: '我们的使命',
                  desc: '打造最优质的同人内容聚合平台，让每一位同人爱好者都能在这里发现触动心灵的作品，让同人创作的文化生态更加繁荣。',
                },
                {
                  icon: 'ri-eye-line',
                  title: '我们的愿景',
                  desc: '成为华语地区最具影响力的同人内容聚合平台，搭建创作者与读者之间的桥梁，推动同人文化走向更广阔的天地。',
                },
                {
                  icon: 'ri-star-line',
                  title: '我们的价值观',
                  desc: '尊重每一位创作者的劳动成果，坚持高质量的内容筛选标准，维护友好包容的社区氛围，让热爱成为一切的动力。',
                },
              ].map((item) => (
                <div key={item.title} className="text-center p-6 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-600">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats - Reputation */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              平台数据
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { number: '5000+', label: '精选文章', icon: 'ri-article-line' },
                { number: '200+', label: '合作创作者', icon: 'ri-user-star-line' },
                { number: '50万+', label: '月活跃读者', icon: 'ri-group-line' },
                { number: '98%', label: '读者好评率', icon: 'ri-thumb-up-line' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-5">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100/80 text-accent-600">
                    <i className={`${stat.icon} text-xl`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-accent-600 font-heading mb-1">
                    {stat.number}
                  </div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {[
                {
                  name: '星海旅人',
                  role: '同人小说作者',
                  quote: '今日同人库是我每天必逛的平台。编辑的推荐非常用心，经常能发现一些我之前不知道的优秀作品。作为创作者，被收录到精选推荐中也给了我很大的鼓励。',
                },
                {
                  name: '墨染青衫',
                  role: '同人画师',
                  quote: '平台的分类和推荐做得非常好，读者也很活跃。我的画作在这里得到了很多真诚的反馈，这对我来说是最宝贵的。真心推荐给所有同人爱好者。',
                },
                {
                  name: '夜空P',
                  role: 'Vocaloid P主',
                  quote: '在同人音乐推荐方面，今日同人库的编辑团队有着非常专业的眼光。他们推荐的曲目覆盖面广，从热门到小众都有，让我发现了不少宝藏创作者。',
                },
              ].map((testimonial) => (
                <div key={testimonial.name} className="p-6 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{testimonial.quote}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-heading font-bold text-sm">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{testimonial.name}</div>
                      <div className="text-xs text-foreground-400">{testimonial.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              常见问题
            </h2>
            <div className="space-y-4">
              {[
                { q: '今日同人库是什么？', a: '今日同人库是一个专为同人爱好者打造的每日内容聚合平台。我们每日精选优质的原创同人小说、同人画作、同人音乐和周边资讯，帮助同人爱好者发现更多精彩内容。' },
                { q: '如何向今日同人库投稿？', a: '我们欢迎所有同人创作者的投稿！你可以通过下方"联系我们"表单发送作品链接和简介，我们的编辑团队会在3个工作日内审核并回复。' },
                { q: '内容多久更新一次？', a: '今日同人库每天更新！我们每日精选最新的同人作品，确保你每次访问都能发现新鲜有趣的内容。订阅我们的邮件通知可以第一时间收到每日精选推送。' },
                { q: '今日同人库是否收费？', a: '今日同人库完全免费！我们的使命是推广优质的同人创作，让更多人感受到同人文化的魅力。所有内容均可免费浏览。' },
                { q: '如何成为合作伙伴？', a: '如果你是同人展会主办方、同人社团组织者或其他同人相关机构，欢迎通过"联系我们"表单与我们取得联系。' },
              ].map((faq, i) => (
                <div key={i} className="bg-background-100 rounded-lg p-5 border border-background-200/60">
                  <h4 className="font-heading font-semibold text-foreground-900 mb-2">
                    <a href={`#faq-${i}`} id={`faq-${i}`}>{faq.q}</a>
                  </h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-3">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              有任何问题、建议或合作意向？欢迎通过以下表单联系我们
            </p>

            {formStatus === 'success' ? (
              <div className="text-center p-8 bg-background-50 rounded-lg border border-background-200/70">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-2">消息已发送！</h3>
                <p className="text-sm text-foreground-500">感谢你的来信，我们会尽快回复。</p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="space-y-4 bg-background-50 p-6 rounded-lg border border-background-200/70"
              >
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入你的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入你的邮箱地址"
                  />
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题 <span className="text-primary-500">*</span>
                  </label>
                  <select
                    id="contact-subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-foreground-900 text-sm focus:outline-none focus:border-primary-300 transition-colors cursor-pointer"
                  >
                    <option value="">请选择主题</option>
                    <option value="投稿咨询">投稿咨询</option>
                    <option value="合作意向">合作意向</option>
                    <option value="问题反馈">问题反馈</option>
                    <option value="其他建议">其他建议</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    placeholder="请输入你的留言内容（最多500字）"
                  ></textarea>
                </div>
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full py-3 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-sm disabled:opacity-60"
                >
                  {formStatus === 'loading' ? '发送中...' : '发送消息'}
                </button>

                {formStatus === 'error' && formError && (
                  <p className="text-sm text-red-600 text-center mt-2">
                    {formError}
                  </p>
                )}
              </form>
            )}
          </div>
        </section>
      </div>
    </>
  );
}